import { Fragment } from 'react';
import classNames from 'classnames';
import { useFormikContext } from 'formik';
import { Input, Button } from '@crazyegginc/hatch';

import { AUDIENCE_METADATA } from '../../../metadata';
import { generateEmptyConditionRow } from '../../../utils';
import { ConditionTooltip } from '../ConditionTooltip';

import { ReactComponent as CrossIcon } from '@crazyegginc/hatch/dist/images/icon-cross.svg';

export function TextValue({ index, innerIndex, rowActions, valueActions, extra }) {
  const { values, errors, touched, handleChange, handleBlur, setFieldValue } = useFormikContext();
  const condition = values.conditions[index];
  const { criteria, rows } = condition;
  const row = rows[innerIndex];
  const v = row.values;
  const isLastRow = innerIndex === rows.length - 1;
  const valueTooltips = row.valueTooltips ?? [];

  const metadata = AUDIENCE_METADATA[criteria];
  const { multiOr, multiAnd, placeholder } = metadata;

  function onTooltipDismiss(valueIndex) {
    setFieldValue(
      `conditions[${index}].rows[${innerIndex}].valueTooltips`,
      valueTooltips.filter((tooltipIndex) => tooltipIndex !== valueIndex),
      false,
    );
  }

  return (
    <div className="flex flex-wrap gap-y-2.5">
      {v.map((value, i) => {
        const valueInput = (
          <div className="relative w-[250px]">
            <Input
              type="text"
              id={`conditions[${index}].rows[${innerIndex}].values[${i}]`}
              name={`conditions[${index}].rows[${innerIndex}].values[${i}]`}
              value={value}
              placeholder={placeholder}
              onChange={handleChange}
              onBlur={handleBlur}
              aria-label={criteria}
              className="!pl-2.5"
              error={
                touched.conditions?.[index]?.rows?.[innerIndex]?.values?.[i]
                  ? (errors.conditions?.[index]?.rows?.[innerIndex]?.values?.[i] ?? null)
                  : null
              }
            />
            {v.length > 1 || rows.length > 1 ? (
              <button
                type="button"
                onClick={() => {
                  if (v.length > 1) {
                    // remove values from row
                    valueActions.remove(i);
                  } else {
                    // if only one value left, remove the whole row
                    rowActions.remove(innerIndex);
                  }
                }}
                className={classNames(
                  'absolute right-0 top-0 flex h-[35px] w-[35px] items-center justify-center',
                  'text-cadet-blue-500 hover:text-radical-red-500 focus:text-radical-red-500 focus-visible:outline-black',
                )}
              >
                <CrossIcon className="h-2.5 w-2.5 fill-current" aria-label="delete condition value" />
              </button>
            ) : null}
          </div>
        );

        return (
          <Fragment key={i}>
            {multiOr && i > 0 ? (
              <div className="text-body-5 font-semibold ml-2.5 flex h-[35px] w-[47px] items-center justify-center">
                or
              </div>
            ) : null}
            <ConditionTooltip
              criteria={metadata}
              show={valueTooltips.includes(i)}
              onDismiss={() => onTooltipDismiss(i)}
            >
              {valueInput}
            </ConditionTooltip>
          </Fragment>
        );
      })}
      {multiOr ? (
        <Button variant="secondary" onClick={() => valueActions.push('')} className="!px-2.5 ml-2.5">
          or...
        </Button>
      ) : null}
      {multiAnd ? (
        <Button
          variant="secondary"
          onClick={() => rowActions.insert(innerIndex + 1, generateEmptyConditionRow(criteria))}
          className="!px-2.5 ml-2.5"
        >
          and...
        </Button>
      ) : null}
      {extra && isLastRow ? extra : null}
    </div>
  );
}
